'use client';

import type { ErrorInfo, JSX, ReactNode } from 'react';
import { Component } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { reportError } from '@/lib/telemetry/client';

// ---------------------------------------------------------------------------
// Props & State
// ---------------------------------------------------------------------------

interface TabErrorBoundaryProps {
  children: ReactNode;
  tabName: string;
}

interface TabErrorBoundaryState {
  hasError: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export class TabErrorBoundary extends Component<TabErrorBoundaryProps, TabErrorBoundaryState> {
  constructor(props: TabErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): TabErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      component: 'TabErrorBoundary',
      tab: this.props.tabName,
      componentStack: info.componentStack ?? '',
    });
  }

  override render(): JSX.Element | ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-body text-foreground-secondary">
            Something went wrong loading this tab.
          </p>
          <AnimatedButton
            variant="secondary"
            size="sm"
            onClick={(): void => this.setState({ hasError: false })}
          >
            Try again
          </AnimatedButton>
        </div>
      );
    }

    return this.props.children;
  }
}
